import React, { useMemo } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DefaultLayout } from '../layouts/defaultLayout';
import { MyPets } from '../pages/myPets/myPets';
import { Claims } from '../pages/claims/claims';
import { NewClaim } from '../pages/newClaim/newClaim';
import { LiveVet } from '../pages/liveVet/liveVet';
import { MyDocuments } from '../pages/myDocuments/myDocuments';
import { PremiumsAndPayments } from '../pages/premiumsAndPayments/history/premiumsAndPayments';
import { BankDetails } from '../pages/premiumsAndPayments/bankDetails/bankDetails';
import { Payment } from '../pages/premiumsAndPayments/payment/payment';
import { HelpCenterNav } from '../components/helpCentre/helpCenterNav/helpCenterNav';
import { MyProfileSection } from '../components/myProfile/myProfileSection/myProfileSection';
import { Logout } from '../components/authentication/logout/logout';
import { ContactUs } from '../pages/contactUs/contactUs';
import { AuthErrorPage } from '../components/authError/authErrorPage';
import { PostConsultation } from '../components/liveVet/postConsultation/postConsultation';
import Login from '../components/authentication/login';
import { CSPUrlsMap } from './CSPUrlsMap';
import VerificationByEmail from '../pages/verification/verificationByEmail';
import ProtectedRoute from './ProtectedRoute';
import VerificationBySms from '../pages/verification/verificationBySms';
import Verification from '../pages/verification/verification';
import { SLICES } from '../constants/slices';
import UpdateEmail from '../pages/updateContactDetails/updateEmail/updateEmail';
import UpdatePhone from '../pages/updateContactDetails/updatePhone/updatePhone';
import UploadRequiredDocuments from '../pages/uploadRequiredDocuments/uploadRequiredDocuments';
import { DashboardWrapper } from '../pages/dashboard/dashboardWrapper';
import { Renewal } from '../pages/renewal/renewal';
import { RenewalConfirmation } from '../pages/renewal/confirmation/confirmation';

export const CSPRouterProvider = () => {
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);

  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route
            element={<DefaultLayout />}
            errorElement={<div>Something went wrong</div>}
          >
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <Navigate
                  replace
                  to={CSPUrlsMap.login.url}
                />
              }
            />
            <Route
              path={CSPUrlsMap.login.url}
              element={<Login />}
            />
            <Route
              path={CSPUrlsMap.registration.url}
              element={<Login signup />}
            />
            <Route
              path={CSPUrlsMap.verification.url}
              element={<Verification />}
            />
            <Route
              path={CSPUrlsMap.postConsultation.url}
              element={<PostConsultation />}
            />
            <Route
              path={CSPUrlsMap.verifyEmail.url}
              element={<VerificationByEmail />}
            />
            <Route
              path={CSPUrlsMap.verifySms.url}
              element={<VerificationBySms />}
            />
            <Route
              path={CSPUrlsMap.authErrorPage.url}
              element={<AuthErrorPage />}
            />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route
                path={CSPUrlsMap.dashboard.url}
                element={<DashboardWrapper />}
              />
              <Route
                path={CSPUrlsMap.contactUs.url}
                element={<ContactUs />}
              />
              <Route
                path={CSPUrlsMap.myPets.url}
                element={<MyPets />}
              />
              <Route
                path={CSPUrlsMap.myClaims.url}
                element={<Claims />}
              />
              <Route
                path={CSPUrlsMap.newClaim.url}
                element={<NewClaim />}
              />
              <Route
                path={CSPUrlsMap.myDocuments.url}
                element={<MyDocuments />}
              />
              <Route
                path={CSPUrlsMap.premPayHistory.url}
                element={<PremiumsAndPayments />}
              />
              <Route
                path={CSPUrlsMap.bankDetails.url}
                element={<BankDetails />}
              />
              <Route
                path={CSPUrlsMap.payNow.url}
                element={<Payment />}
              />
              <Route
                path={CSPUrlsMap.helpCentre.url}
                element={<HelpCenterNav activeTopic="popular" />}
              />
              <Route
                path={CSPUrlsMap.accountDetails.url}
                element={<MyProfileSection activeTopic="accountDetails" />}
              />
              <Route
                path={CSPUrlsMap.vetDetails.url}
                element={<MyProfileSection activeTopic="vetDetails" />}
              />
              <Route
                path={CSPUrlsMap.notificationPreferences.url}
                element={<MyProfileSection activeTopic="notificationPreferences" />}
              />
              <Route
                path={CSPUrlsMap.updateEmail.url}
                element={<UpdateEmail />}
              />
              <Route
                path={CSPUrlsMap.updateMobile.url}
                element={<UpdatePhone />}
              />
              <Route
                path={CSPUrlsMap.helpCentreGettingStarted.url}
                element={<HelpCenterNav activeTopic="gettingStarted" />}
              />
              <Route
                path={CSPUrlsMap.helpCentrePolicyManagement.url}
                element={<HelpCenterNav activeTopic="management" />}
              />
              <Route
                path={CSPUrlsMap.helpCentreClaims.url}
                element={<HelpCenterNav activeTopic="claims" />}
              />
              <Route
                path={CSPUrlsMap.helpCentrePremiumsPayments.url}
                element={<HelpCenterNav activeTopic="premiums" />}
              />
              <Route
                path={CSPUrlsMap.helpCentrePolicyCoverage.url}
                element={<HelpCenterNav activeTopic="coverage" />}
              />
              <Route
                path={CSPUrlsMap.logout.url}
                element={<Logout />}
              />
              <Route
                path={CSPUrlsMap.liveVet.url}
                element={<LiveVet />}
              />
              <Route
                path={CSPUrlsMap.uploadRequiredDocuments.url}
                element={<UploadRequiredDocuments />}
              />
              <Route
                path={CSPUrlsMap.renewalHub.url}
                element={<Renewal />}
              />
              <Route
                path={CSPUrlsMap.renewalHubConfirmation.url}
                element={<RenewalConfirmation />}
              />
            </Route>
          </Route>
        )
      ),
    []
  );

  return <RouterProvider router={router} />;
};

export default CSPRouterProvider;
